import { Link, useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { FloopLogo } from "./FloopLogo";
import { ThemeToggle } from "./ThemeToggle";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect } from "react";
import { 
  Sparkles, 
  Images, 
  Code, 
  History, 
  BookOpen, 
  Video, 
  Newspaper, 
  HelpCircle, 
  Users, 
  Building2, 
  Briefcase, 
  Megaphone, 
  Mail, 
  Handshake,
  User,
  LogOut,
  Settings,
  CreditCard,
  ChevronDown,
  Menu,
  X,
  Zap
} from "lucide-react";
import { Skeleton } from "./ui/skeleton";

const productItems = [
  { title: "Fonctionnalités", href: "/features", description: "Découvrez toutes nos capacités IA", icon: Sparkles },
  { title: "Galerie", href: "/gallery", description: "Explorez les créations de la communauté", icon: Images },
  { title: "API", href: "/api", description: "Intégrez Floop dans vos projets", icon: Code },
  { title: "Changelog", href: "/changelog", description: "Suivez nos dernières mises à jour", icon: History },
];

const resourceItems = [
  { title: "Documentation", href: "/documentation", description: "Guides complets et références", icon: BookOpen },
  { title: "Tutoriels", href: "/tutorials", description: "Apprenez à maîtriser Floop", icon: Video },
  { title: "Blog", href: "/blog", description: "Actualités et conseils créatifs", icon: Newspaper },
  { title: "Centre d'aide", href: "/help", description: "FAQ et support technique", icon: HelpCircle },
  { title: "Communauté", href: "/community", description: "Rejoignez les créateurs Floop", icon: Users },
];

const companyItems = [
  { title: "À propos", href: "/about", description: "Notre mission et notre équipe", icon: Building2 },
  { title: "Carrières", href: "/careers", description: "Rejoignez l'aventure Floop", icon: Briefcase },
  { title: "Presse", href: "/press", description: "Kit média et actualités", icon: Megaphone },
  { title: "Contact", href: "/contact", description: "Contactez notre équipe", icon: Mail },
  { title: "Partenaires", href: "/partners", description: "Programmes de partenariat", icon: Handshake },
];

interface ListItemProps extends React.ComponentPropsWithoutRef<"a"> {
  title: string;
  icon?: React.ComponentType<{ className?: string }>;
}

const ListItem = ({ className, title, children, icon: Icon, ...props }: ListItemProps) => {
  return (
    <li>
      <NavigationMenuLink asChild>
        <a
          className={cn(
            "block select-none space-y-1 rounded-xl p-3 leading-none no-underline outline-none transition-all hover:bg-secondary hover:text-foreground focus:bg-secondary focus:text-foreground group",
            className
          )}
          {...props}
        >
          <div className="flex items-center gap-2">
            {Icon && <Icon className="w-4 h-4 text-primary group-hover:text-accent transition-colors" />}
            <div className="text-sm font-medium leading-none text-foreground">{title}</div>
          </div>
          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
            {children}
          </p>
        </a>
      </NavigationMenuLink>
    </li>
  );
};

export const Header = () => {
  const { isAuthenticated, loading, getUserDisplayName, getUserEmail, signOut } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [announcementBarVisible, setAnnouncementBarVisible] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    
    const handleAnnouncementChange = (e: CustomEvent<{ visible: boolean }>) => {
      setAnnouncementBarVisible(e.detail.visible);
    };
    window.addEventListener('announcementBarChange', handleAnnouncementChange as EventListener);
    
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener('announcementBarChange', handleAnnouncementChange as EventListener);
    };
  }, []);

  const handleSignOut = async () => {
    const { error } = await signOut();
    if (error) {
      toast({
        title: "Erreur",
        description: "Erreur lors de la déconnexion",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Déconnexion",
        description: "Vous avez été déconnecté avec succès",
      });
      navigate("/");
    }
  };

  const topOffset = announcementBarVisible ? "top-[44px]" : "top-0";

  return (
    <>
      <header 
        className={cn(
          "fixed left-0 right-0 z-50 transition-all duration-500",
          topOffset,
          scrolled 
            ? "py-3 glass-header" 
            : "py-4 bg-transparent"
        )}
      >
        <div className="container mx-auto px-6 flex items-center justify-between">
          <Link to="/" className="group">
            <FloopLogo size="md" showText={true} className="group-hover:opacity-80 transition-all duration-300" />
          </Link>
          
          <NavigationMenu className="hidden lg:flex">
            <NavigationMenuList className="gap-1">
              <NavigationMenuItem>
                <NavigationMenuTrigger className="bg-transparent text-foreground hover:bg-secondary data-[state=open]:bg-secondary rounded-full px-4">
                  Produit
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[400px] gap-2 p-4 md:w-[500px] md:grid-cols-2 glass-card border border-border/50 rounded-2xl">
                    {productItems.map((item) => (
                      <ListItem
                        key={item.title}
                        title={item.title}
                        href={item.href}
                        icon={item.icon}
                      >
                        {item.description}
                      </ListItem>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
              
              <NavigationMenuItem>
                <NavigationMenuTrigger className="bg-transparent text-foreground hover:bg-secondary data-[state=open]:bg-secondary rounded-full px-4">
                  Ressources
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[400px] gap-2 p-4 md:w-[500px] md:grid-cols-2 glass-card border border-border/50 rounded-2xl">
                    {resourceItems.map((item) => (
                      <ListItem
                        key={item.title}
                        title={item.title}
                        href={item.href}
                        icon={item.icon}
                      >
                        {item.description}
                      </ListItem>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
              
              <NavigationMenuItem>
                <NavigationMenuTrigger className="bg-transparent text-foreground hover:bg-secondary data-[state=open]:bg-secondary rounded-full px-4">
                  Entreprise
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[400px] gap-2 p-4 md:w-[500px] md:grid-cols-2 glass-card border border-border/50 rounded-2xl">
                    {companyItems.map((item) => (
                      <ListItem
                        key={item.title}
                        title={item.title}
                        href={item.href}
                        icon={item.icon}
                      >
                        {item.description}
                      </ListItem>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
              
              <NavigationMenuItem>
                <NavigationMenuLink asChild>
                  <Link to="/pricing" className="group inline-flex h-10 w-max items-center justify-center rounded-full bg-transparent px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-secondary focus:bg-secondary focus:outline-none">
                    Tarifs
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>
          
          <nav className="flex items-center gap-2">
            <ThemeToggle />
            {loading ? (
              <Skeleton className="h-10 w-28 rounded-full" />
            ) : isAuthenticated ? (
              <>
                <Link to="/create" className="hidden sm:block">
                  <Button variant="ghost" className="text-foreground hover:bg-secondary rounded-full">
                    Créer
                  </Button>
                </Link>
                <Link to="/payment" className="hidden sm:block">
                  <Button className="bg-gradient-floop hover:opacity-90 text-white rounded-full px-4 shadow-lg shadow-primary/30">
                    <Zap className="w-4 h-4 mr-1" />
                    Acheter
                  </Button>
                </Link>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2 rounded-full hover:bg-secondary">
                      <div className="w-8 h-8 rounded-full bg-gradient-floop flex items-center justify-center ring-2 ring-border">
                        <User className="w-4 h-4 text-white" />
                      </div>
                      <span className="hidden sm:inline text-foreground">{getUserDisplayName()}</span>
                      <ChevronDown className="w-4 h-4 text-muted-foreground" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 glass-card border-border/50 rounded-xl">
                    <div className="px-3 py-2 border-b border-border/50">
                      <p className="text-sm font-medium text-foreground">{getUserDisplayName()}</p>
                      <p className="text-xs text-muted-foreground">{getUserEmail()}</p>
                    </div>
                    <DropdownMenuItem asChild className="cursor-pointer rounded-lg mt-1 focus:bg-secondary">
                      <Link to="/create">
                        <Sparkles className="w-4 h-4 mr-2" />
                        Dashboard
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="cursor-pointer rounded-lg focus:bg-secondary">
                      <Link to="/credits">
                        <CreditCard className="w-4 h-4 mr-2" />
                        Mes crédits
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="cursor-pointer rounded-lg focus:bg-secondary">
                      <Link to="/settings">
                        <Settings className="w-4 h-4 mr-2" />
                        Paramètres
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-border/50" />
                    <DropdownMenuItem 
                      onClick={handleSignOut}
                      className="cursor-pointer rounded-lg text-destructive focus:text-destructive focus:bg-destructive/10"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Se déconnecter
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Link to="/create" className="hidden sm:block">
                  <Button variant="ghost" className="text-foreground hover:bg-secondary rounded-full">
                    Créer
                  </Button>
                </Link>
                <Link to="/auth">
                  <Button className="bg-gradient-floop hover:opacity-90 text-white rounded-full px-6 shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 transition-all duration-300 hover:scale-105">
                    Se connecter
                  </Button>
                </Link>
              </>
            )}
            
            {/* Mobile menu button */}
            <button 
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden p-2 rounded-full hover:bg-secondary transition-colors"
              aria-label="Open menu"
            >
              <Menu className="w-6 h-6 text-foreground" />
            </button>
          </nav>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div 
        className={cn(
          "fixed inset-0 z-50 lg:hidden transition-all duration-500",
          mobileMenuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        )}
      >
        <div className="absolute inset-0 bg-background/95 backdrop-blur-xl" onClick={() => setMobileMenuOpen(false)} />
        
        <div className={cn(
          "absolute inset-y-0 right-0 w-full max-w-sm glass-card border-l border-border/50 p-6 transition-transform duration-500",
          mobileMenuOpen ? "translate-x-0" : "translate-x-full"
        )}>
          <div className="flex items-center justify-between mb-8">
            <FloopLogo size="md" />
            <button 
              onClick={() => setMobileMenuOpen(false)}
              className="p-2 rounded-full hover:bg-secondary transition-colors"
              aria-label="Close menu"
            >
              <X className="w-6 h-6 text-foreground" />
            </button>
          </div>
          
          <nav className="space-y-6">
            <div>
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Produit</h3>
              <div className="space-y-2">
                {productItems.map((item) => (
                  <Link 
                    key={item.title}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-secondary transition-colors"
                  >
                    <item.icon className="w-5 h-5 text-primary" />
                    <span className="text-foreground">{item.title}</span>
                  </Link>
                ))}
              </div>
            </div>
            
            <div>
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Ressources</h3>
              <div className="space-y-2">
                {resourceItems.slice(0, 3).map((item) => (
                  <Link 
                    key={item.title}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-secondary transition-colors"
                  >
                    <item.icon className="w-5 h-5 text-primary" />
                    <span className="text-foreground">{item.title}</span>
                  </Link>
                ))}
              </div>
            </div>
            
            <Link 
              to="/pricing"
              onClick={() => setMobileMenuOpen(false)}
              className="block"
            >
              <Button className="w-full bg-gradient-floop hover:opacity-90 text-white rounded-full">
                Voir les tarifs
              </Button>
            </Link>
            
            {!isAuthenticated && (
              <Link 
                to="/auth"
                onClick={() => setMobileMenuOpen(false)}
                className="block"
              >
                <Button variant="outline" className="w-full rounded-full border-border hover:bg-secondary">
                  Se connecter
                </Button>
              </Link>
            )}
          </nav>
        </div>
      </div>
    </>
  );
};
